import {
  IUserRepository,
  INotificationRepository,
  type CreateNotificationData,
} from '../repository/interfaces';
import { INotificationsUseCase } from './interfaces';

/**
 * Notifications Use Case
 * Handles email and in-app notifications
 * User Stories: 17, 18
 */
export class NotificationsUseCase implements INotificationsUseCase {
  constructor(
    private userRepo: IUserRepository,
    private notificationRepo: INotificationRepository,
  ) {}

  private logInfo(message: string): void {
    console.log(`[NotificationsUseCase] ${message}`);
  }

  private mapBulkTypeToInAppType(t: string): CreateNotificationData['type'] {
    const x = String(t || '').toLowerCase();
    if (x === 'success' || x === 'warning' || x === 'error' || x === 'system' || x === 'info') {
      return x;
    }
    return 'info';
  }

  private mapBulkCategoryToInAppCategory(c: string): CreateNotificationData['category'] {
    const x = String(c || '').toLowerCase();
    const allowed: CreateNotificationData['category'][] = [
      'course',
      'connect',
      'payment',
      'message',
      'system',
      'achievement',
      'reminder',
    ];
    if (allowed.includes(x as CreateNotificationData['category'])) {
      return x as CreateNotificationData['category'];
    }
    return 'system';
  }

  /**
   * Send email notification for critical flows
   * User Story 17: As a user, I receive email in critical flows (verify, purchase receipt, enrollment, connect status)
   */
  async sendEmailNotification(
    userId: string,
    notificationType: 'emailVerification' | 'passwordReset' | 'purchaseReceipt' | 'enrollmentConfirmation' | 'connectStatus' | 'courseCompletion' | 'subscriptionRenewal',
    data: Record<string, any>
  ): Promise<{
    notification: any;
    emailSent: boolean;
    deliveryId?: string;
  }> {
    try {
      // Get user
      const user = await this.userRepo.getUserById(userId);
      if (!user) {
        throw new Error('User not found');
      }

      // Generate email content based on type
      const emailContent = await this.generateEmailContent(notificationType, user, data);

      // Send email (mock implementation)
      const emailSent = await this.sendEmail(
        user.email,
        emailContent.subject,
        emailContent.html,
        emailContent.text
      );

      // Create notification record
      const notification = {
        id: `email_${Date.now()}`,
        userId: userId,
        type: 'email',
        category: notificationType,
        title: emailContent.subject,
        content: emailContent.text,
        status: emailSent ? 'sent' : 'failed',
        sentAt: emailSent ? new Date() : null,
        deliveryId: emailSent ? `delivery_${Date.now()}` : null,
        metadata: {
          emailType: notificationType,
          recipientEmail: user.email,
          ...data
        }
      };

      return {
        notification,
        emailSent,
        deliveryId: notification.deliveryId
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Send connect notification to professional
   * User Story 18: As a professional, I get alerts on new requests and messages
   */
  async sendConnectNotification(
    professionalId: string,
    requestId: string,
    notificationType: 'newRequest' | 'requestAccepted' | 'requestDeclined' | 'requestCancelled' | 'requestReminder',
    data: Record<string, any>
  ): Promise<{
    notification: any;
    emailSent: boolean;
    pushSent: boolean;
  }> {
    try {
      // Get professional
      const professional = await this.userRepo.getUserById(professionalId);
      if (!professional) {
        throw new Error('Professional not found');
      }

      // Generate notification content
      const content = await this.generateConnectNotificationContent(notificationType, data);

      // Send email notification
      const emailSent = await this.sendEmail(
        professional.email,
        content.subject,
        content.html,
        content.text
      );

      // Send push notification (mock)
      const pushSent = await this.sendPushNotification(professionalId, {
        title: content.subject,
        body: content.text,
        data: {
          requestId,
          type: notificationType,
          ...data
        }
      });

      // Create notification record
      const notification = {
        id: `connect_notif_${Date.now()}`,
        userId: professionalId,
        type: 'connectAlert',
        category: notificationType,
        title: content.subject,
        content: content.text,
        status: 'sent',
        sentAt: new Date(),
        readAt: null,
        metadata: {
          requestId,
          emailSent: emailSent,
          pushSent: pushSent,
          ...data
        }
      };

      return {
        notification,
        emailSent,
        pushSent
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Send bulk notifications
   */
  async sendBulkNotifications(
    userIds: string[],
    notificationData: {
      type: string;
      category: string;
      title: string;
      content: string;
      metadata?: Record<string, any>;
    },
    channels: ('email' | 'push' | 'inApp')[] = ['inApp']
  ): Promise<{
    sent: number;
    failed: number;
    results: Array<{
      userId: string;
      success: boolean;
      channels: Record<string, boolean>;
      error?: string;
    }>;
  }> {
    try {
      const results: Array<{
        userId: string;
        success: boolean;
        channels: Record<string, boolean>;
        error?: string;
      }> = [];

      let sent = 0;
      let failed = 0;

      for (const userId of userIds) {
        try {
          const user = await this.userRepo.getUserById(userId);
          if (!user) {
            results.push({
              userId,
              success: false,
              channels: {},
              error: 'User not found'
            });
            failed++;
            continue;
          }

          const channelsResult: Record<string, boolean> = {};

          // Send to each requested channel
          for (const channel of channels) {
            try {
              switch (channel) {
                case 'email':
                  channelsResult.email = await this.sendEmail(
                    user.email,
                    notificationData.title,
                    this.convertToHtml(notificationData.content),
                    notificationData.content
                  );
                  break;
                case 'push':
                  channelsResult.push = await this.sendPushNotification(userId, {
                    title: notificationData.title,
                    body: notificationData.content,
                    data: notificationData.metadata
                  });
                  break;
                case 'inApp':
                  channelsResult.inApp = await this.createInAppNotification(userId, notificationData);
                  break;
              }
            } catch (error) {
              channelsResult[channel] = false;
            }
          }

          // Check if at least one channel succeeded
          const success = Object.values(channelsResult).some(sent => sent);

          results.push({
            userId,
            success,
            channels: channelsResult
          });

          if (success) {
            sent++;
          } else {
            failed++;
          }

        } catch (error) {
          results.push({
            userId,
            success: false,
            channels: {},
            error: error instanceof Error ? error.message : 'Unknown error'
          });
          failed++;
        }
      }

      return {
        sent,
        failed,
        results
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Get user's notifications
   */
  async getUserNotifications(
    userId: string,
    options: {
      type?: string;
      category?: string;
      status?: 'unread' | 'read' | 'all';
      limit?: number;
      offset?: number;
    } = {}
  ): Promise<{
    notifications: any[];
    stats: {
      total: number;
      unread: number;
      read: number;
    };
    pagination: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  }> {
    try {
      // Mock notifications data
      const mockNotifications = this.generateMockNotifications(userId, 25);

      // Apply filters
      let filteredNotifications = mockNotifications;

      if (options.type) {
        filteredNotifications = filteredNotifications.filter(n => n.type === options.type);
      }

      if (options.category) {
        filteredNotifications = filteredNotifications.filter(n => n.category === options.category);
      }

      if (options.status === 'unread') {
        filteredNotifications = filteredNotifications.filter(n => !n.readAt);
      } else if (options.status === 'read') {
        filteredNotifications = filteredNotifications.filter(n => n.readAt);
      }

      // Sort by creation date (newest first)
      filteredNotifications.sort((a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      // Apply pagination
      const total = filteredNotifications.length;
      const limit = options.limit || 20;
      const offset = options.offset || 0;
      const paginatedNotifications = filteredNotifications.slice(offset, offset + limit);

      // Calculate stats
      const stats = {
        total: mockNotifications.length,
        unread: mockNotifications.filter(n => !n.readAt).length,
        read: mockNotifications.filter(n => n.readAt).length
      };

      return {
        notifications: paginatedNotifications,
        stats,
        pagination: {
          total,
          limit,
          offset,
          hasMore: offset + limit < total
        }
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Mark notifications as read
   */
  async markNotificationsAsRead(
    userId: string,
    notificationIds: string[]
  ): Promise<{
    marked: number;
    notifications: any[];
  }> {
    try {
      // Mock marking as read
      const markedNotifications = notificationIds.map(id => ({
        id,
        readAt: new Date(),
        userId: userId
      }));

      return {
        marked: notificationIds.length,
        notifications: markedNotifications
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Update user notification preferences
   */
  async updateNotificationPreferences(
    userId: string,
    preferences: {
      email: {
        marketing: boolean;
        requests: boolean;
        courses: boolean;
        system: boolean;
      };
      push: {
        requests: boolean;
        messages: boolean;
        reminders: boolean;
      };
      inApp: {
        all: boolean;
        mentions: boolean;
        replies: boolean;
      };
    }
  ): Promise<{
    user: any;
    preferences: any;
  }> {
    try {
      // Get user
      const user = await this.userRepo.getUserById(userId);
      if (!user) {
        throw new Error('User not found');
      }

      // Update user with preferences (mock)
      const updatedUser = {
        ...user,
        notificationPreferences: preferences,
        updatedAt: new Date()
      };

      return {
        user: updatedUser,
        preferences
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Send system-wide announcement
   */
  async sendSystemAnnouncement(
    senderId: string,
    announcement: {
      title: string;
      message: string;
      priority: 'low' | 'normal' | 'high' | 'urgent';
      targetUsers?: string[];
      channels: ('email' | 'push' | 'inApp')[];
    }
  ): Promise<{
    sent: number;
    failed: number;
    announcement: any;
  }> {
    try {
      // Verify sender has permission (admin)
      const isAdmin = await this.userRepo.isUserAdmin(senderId);
      if (!isAdmin) {
        throw new Error('Unauthorized: Only administrators can send system announcements');
      }

      // Create announcement record
      const announcementRecord = {
        id: `announcement_${Date.now()}`,
        senderId: senderId,
        title: announcement.title,
        message: announcement.message,
        priority: announcement.priority,
        targetUsers: announcement.targetUsers,
        channels: announcement.channels,
        sentAt: new Date(),
        status: 'sending'
      };

      // Send to target users
      const targetUserIds = announcement.targetUsers || await this.getAllActiveUserIds();

      const result = await this.sendBulkNotifications(
        targetUserIds,
        {
          type: 'systemAnnouncement',
          category: 'announcement',
          title: announcement.title,
          content: announcement.message,
          metadata: {
            priority: announcement.priority,
            senderId: senderId
          }
        },
        announcement.channels
      );

      // Update announcement status
      announcementRecord.status = 'sent';
      announcementRecord.sentCount = result.sent;
      announcementRecord.failedCount = result.failed;

      return {
        sent: result.sent,
        failed: result.failed,
        announcement: announcementRecord
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  // Helper methods

  private async generateEmailContent(
    type: string,
    user: any,
    data: Record<string, any>
  ): Promise<{
    subject: string;
    html: string;
    text: string;
  }> {
    const templates: Record<string, any> = {
      emailVerification: {
        subject: 'Verify your email address',
        html: `<h1>Welcome to Dance RealmX!</h1><p>Please verify your email by clicking the link below.</p>`,
        text: 'Welcome to Dance RealmX! Please verify your email.'
      },
      passwordReset: {
        subject: 'Reset your password',
        html: `<h1>Password Reset</h1><p>Click the link below to reset your password.</p>`,
        text: 'Click the link to reset your password.'
      },
      purchaseReceipt: {
        subject: `Receipt for your purchase - $${data.amount}`,
        html: `<h1>Thank you for your purchase!</h1><p>You purchased: ${data.itemName}</p>`,
        text: `Thank you for your purchase of ${data.itemName}`
      }
    };

    const template = templates[type] || {
      subject: 'Notification from Dance RealmX',
      html: '<h1>Notification</h1><p>You have a new notification.</p>',
      text: 'You have a new notification.'
    };

    return template;
  }

  private async generateConnectNotificationContent(
    type: string,
    data: Record<string, any>
  ): Promise<{
    subject: string;
    html: string;
    text: string;
  }> {
    const templates: Record<string, any> = {
      newRequest: {
        subject: 'New Request',
        html: `<h1>New Request</h1><p>You have received a new request from ${data.clientName}.</p>`,
        text: `New request from ${data.clientName}`
      },
      requestAccepted: {
        subject: 'Request Confirmed',
        html: `<h1>Request Confirmed</h1><p>Your request has been accepted.</p>`,
        text: 'Your request has been accepted.'
      }
    };

    return templates[type] || {
      subject: 'Request Update',
      html: '<h1>Request Update</h1><p>Your request status has been updated.</p>',
      text: 'Your request status has been updated.'
    };
  }

  private async sendEmail(
    to: string,
    subject: string,
    html: string,
    text: string
  ): Promise<boolean> {
    // Mock email sending
    this.logInfo(`Email sent to ${to}: ${subject}`);
    await new Promise(resolve => setTimeout(resolve, 200)); // Simulate sending time
    return Math.random() > 0.05; // 95% success rate
  }

  private async sendPushNotification(
    userId: string,
    notification: {
      title: string;
      body: string;
      data?: Record<string, any>;
    }
  ): Promise<boolean> {
    // Mock push notification sending
    this.logInfo(`Push notification sent to ${userId}: ${notification.title}`);
    return Math.random() > 0.1; // 90% success rate
  }

  private async createInAppNotification(
    userId: string,
    notificationData: {
      type: string;
      category: string;
      title: string;
      content: string;
      metadata?: Record<string, any>;
    }
  ): Promise<boolean> {
    try {
      await this.notificationRepo.createNotification({
        userId,
        title: notificationData.title,
        message: notificationData.content,
        type: this.mapBulkTypeToInAppType(notificationData.type),
        category: this.mapBulkCategoryToInAppCategory(notificationData.category),
        isRead: false,
        priority: 'normal',
        metadata: notificationData.metadata,
      });
      this.logInfo(`In-app notification persisted for ${userId}: ${notificationData.title}`);
      return true;
    } catch (e) {
      console.error('[NotificationsUseCase] createInAppNotification failed', e);
      return false;
    }
  }

  private convertToHtml(text: string): string {
    return `<div>${text.replace('\n', '<br>')}</div>`;
  }

  private generateMockNotifications(userId: string, count: number): any[] {
    const notifications = [];
    const types = ['connectAlert', 'courseUpdate', 'systemAnnouncement', 'message'];
    const categories = ['newRequest', 'courseCompletion', 'announcement', 'messageReceived'];

    for (let i = 0; i < count; i++) {
      const isRead = Math.random() > 0.4; // 60% read
      notifications.push({
        id: `notif_${i}`,
        userId: userId,
        type: types[Math.floor(Math.random() * types.length)],
        category: categories[Math.floor(Math.random() * categories.length)],
        title: `Notification ${i + 1}`,
        content: `This is notification content ${i + 1}. ${['Important update!', 'Check this out.', 'New message received.', 'Action required.'][Math.floor(Math.random() * 4)]}`,
        status: 'sent',
        createdAt: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000),
        readAt: isRead ? new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000) : null,
        metadata: {}
      });
    }
    return notifications;
  }

  private async getAllActiveUserIds(): Promise<string[]> {
    // Mock getting all active user IDs
    return Array.from({ length: 100 }, (_, i) => `user_${i + 1}`);
  }
}
